import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Card } from '@ui/themed';
import { t } from 'i18next';
import { B2BFlowDiagram } from '../../../components/b2b-flow-diagram';
import {
  EmptyStateCtaCard,
  EmptyStateHero,
  HeroPreviewCard,
} from '../../../components/empty-state-hero';
import { isShopify, useEnv } from '../../../context/env-context';
import { useHandlers } from '../../../context/page-context';
import { templateToInitialState, usePriceListTemplates } from '../hooks/use-price-list-templates';

// Mock rows for the hero preview card
const PREVIEW_HEADER = { name: 'Wholesale · EU', discount: '-50%' };
const PREVIEW_ROWS = [
  { was: '$48.00', now: '$24.00' },
  { was: '$120.00', now: '$60.00' },
  { was: '$32.00', now: '$16.00' },
];

export function PriceListEmptyState() {
  const { navigationHandler } = useHandlers();
  const env = useEnv();
  const templates = usePriceListTemplates();
  const shopify = isShopify(env.platform);

  const createAutomaticList = () => {
    const template = templates[0];
    if (template) {
      navigationHandler.navigate('/pricing/lists/create', {
        query: { template: templateToInitialState(template).handle },
      });
    } else {
      // No templates means no platform lists, so automatic pricing has no eligible source and
      // its checkbox will be disabled - don't tick in this case
      navigationHandler.navigate('/pricing/lists/create');
    }
  };

  return (
    <Card hidePadding>
      <div className="flex flex-col overflow-hidden rounded-xl">
        <EmptyStateHero
          eyebrow={t('price_lists.empty_state.eyebrow')}
          title={t('price_lists.empty_state.title')}
          description={t('price_lists.empty_state.description')}
        >
          <PricePreviewCard />
        </EmptyStateHero>

        <div className="hidden border-gray-200 border-b px-10 py-6 min-[1200px]:block">
          <B2BFlowDiagram shopify={shopify} highlightStep="priceList" />
        </div>

        <div className="grid gap-6 px-6 pt-6 pb-6 sm:px-10 sm:pb-10 lg:grid-cols-2">
          <EmptyStateCtaCard
            highlighted
            badge={t('price_lists.empty_state.automatic.badge')}
            title={t('price_lists.empty_state.automatic.title')}
            description={
              shopify
                ? t('price_lists.empty_state.automatic.description_shopify')
                : t('price_lists.empty_state.automatic.description')
            }
            cta={t('price_lists.empty_state.automatic.cta')}
            onClick={createAutomaticList}
          />

          <EmptyStateCtaCard
            title={t('price_lists.empty_state.manual.title')}
            description={t('price_lists.empty_state.manual.description')}
            cta={t('price_lists.empty_state.manual.cta')}
            onClick={() =>
              navigationHandler.navigate('/pricing/lists/create', { query: { mode: 'manual' } })
            }
          />
        </div>
      </div>
    </Card>
  );
}

// Static illustration of a discounted price list, floating on the hero
function PricePreviewCard() {
  return (
    <HeroPreviewCard>
      <div className="flex items-center justify-between border-gray-100 border-b px-1 pb-3">
        <span className="font-mono font-semibold text-gray-500 text-xs">{PREVIEW_HEADER.name}</span>
        <span className="font-mono font-semibold text-platform-primary text-xs">
          {PREVIEW_HEADER.discount}
        </span>
      </div>

      {PREVIEW_ROWS.map((row, i) => (
        <div
          className={cn(
            'flex items-center gap-3 px-1 py-3',
            i === PREVIEW_ROWS.length - 1 && 'opacity-50',
          )}
          key={row.now}
        >
          <span className="size-9 shrink-0 rounded-lg bg-platform-primary/10" />
          <span className="h-1.5 flex-1 rounded-full bg-gray-200" />
          <span className="text-gray-400 text-sm line-through">{row.was}</span>
          <span className="font-semibold text-platform-primary">{row.now}</span>
        </div>
      ))}
    </HeroPreviewCard>
  );
}
